import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatDate, formatNumber, API } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import ExportButtons from "@/components/app/ExportButtons";
import { ArrowUpRight, ArrowDownLeft, X, Plus, Search, FileText, HandCoins, Pencil, Trash2, Eraser, Landmark, Link2, RefreshCw, FileSignature, Download, Settings2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Türkçe-uyumlu arama normalizasyonu
const fold = (s) => (s || "")
  .toLocaleLowerCase("tr-TR")
  .replaceAll("ı", "i").replaceAll("ş", "s").replaceAll("ç", "c")
  .replaceAll("ğ", "g").replaceAll("ü", "u").replaceAll("ö", "o")
  .replaceAll("â", "a").replaceAll("î", "i").replaceAll("û", "u");

const badge = (v) => {
  if (v > 0) return { cls: "text-[#DC2626] bg-[#FEE2E2]", label: "Alacaklıyız" };
  if (v < 0) return { cls: "text-[#F59E0B] bg-[#FEF3C7]", label: "Fazla Tahsilat" };
  return { cls: "text-[#0A0A0A] bg-[#F5F5F5]", label: "Denk" };
};

const STATUS_META = {
  lead: { label: "Aday" }, prospect: { label: "Görüşme" },
  active: { label: "Aktif" }, lost: { label: "Kaybedildi" },
};

export default function CustomerAccountsPage() {
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [showColl, setShowColl] = useState(false);
  const [collForm, setCollForm] = useState({ amount: "", date: new Date().toISOString().slice(0,10), notes: "", invoice_no: "" });
  const [showEntry, setShowEntry] = useState(false);
  const [entryForm, setEntryForm] = useState({
    type: "borc", date: new Date().toISOString().slice(0,10),
    amount: "", notes: "", invoice_no: "",
  });
  const [onlyReceivable, setOnlyReceivable] = useState(false);
  const [editingEvt, setEditingEvt] = useState(null);
  const [editForm, setEditForm] = useState({ date: "", amount: "", notes: "", invoice_no: "" });
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({ name: "", sector: "", contact_person: "", phone: "", email: "", tax_office: "", tax_no: "", payment_terms: "", notes: "" });

  // Banka ödemesi eşleştirme (Firma Cari)
  const [showRecon, setShowRecon] = useState(false);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconData, setReconData] = useState(null);
  const [reconSelected, setReconSelected] = useState({}); // { bank_tx_id: company_id_selected }
  const [reconYear, setReconYear] = useState(new Date().getFullYear());

  // Karşılaştırma raporu (matching status)
  const [showMatch, setShowMatch] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [matchYear, setMatchYear] = useState(new Date().getFullYear());
  const [matchDayTol, setMatchDayTol] = useState(3);
  const [matchAmtTol, setMatchAmtTol] = useState(1);
  const [matchTab, setMatchTab] = useState("cari_only");

  // Mutabakat mektubu
  const [mutCompany, setMutCompany] = useState(null);          // seçili firma
  const [mutMonth, setMutMonth] = useState(new Date().toISOString().slice(0, 7));
  const [mutData, setMutData] = useState(null);
  const [mutLoading, setMutLoading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);

  const openMatch = async () => {
    setShowMatch(true); setMatchLoading(true); setMatchData(null);
    try {
      const { data } = await api.get(`/companies-reconcile/matching-status?year=${matchYear}&day_tolerance=${matchDayTol}&amount_tolerance=${matchAmtTol}`);
      setMatchData(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setMatchLoading(false); }
  };

  const openRecon = async () => {
    setShowRecon(true);
    setReconLoading(true);
    setReconData(null);
    try {
      const { data } = await api.get(`/companies-reconcile/bank-preview?year=${reconYear}`);
      setReconData(data);
      // Auto-select rows where a company is confidently detected
      const preSel = {};
      (data.matches || []).forEach(m => {
        if (m.detected_company_id && m.confidence >= 60) preSel[m.bank_tx_id] = m.detected_company_id;
      });
      setReconSelected(preSel);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReconLoading(false); }
  };

  const applyRecon = async () => {
    const chosen = Object.entries(reconSelected).filter(([_, comp]) => !!comp).map(([bt, comp]) => ({ bank_tx_id: bt, company_id: comp }));
    if (chosen.length === 0) { toast.error("En az bir eşleştirmede firma seçin"); return; }
    if (!window.confirm(`${chosen.length} banka hareketi firma cariye 'Gelen Tahsilat' olarak eklenecek. Devam?`)) return;
    try {
      const { data } = await api.post("/companies-reconcile/bank-apply", { matches: chosen });
      toast.success(`${data.applied} tahsilat firma cariye işlendi`);
      setShowRecon(false); setReconData(null); setReconSelected({});
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/companies/statements/summary");
      setSummary(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const openStatement = async (c) => {
    setSelected(c);
    setStatementLoading(true);
    setStatement(null);
    try {
      const { data } = await api.get(`/companies/${c.id}/statement`);
      setStatement(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setStatementLoading(false); }
  };

  const submitCollection = async (e) => {
    e.preventDefault();
    try {
      await api.post("/incoming-payments/quick", {
        company_id: selected.id, company_name: selected.name,
        amount: Number(collForm.amount) || 0,
        date: collForm.date, notes: collForm.notes, invoice_no: collForm.invoice_no,
      });
      toast.success("Tahsilat kaydedildi");
      setShowColl(false); setCollForm({ amount: "", date: new Date().toISOString().slice(0,10), notes: "", invoice_no: "" });
      load(); openStatement(selected);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const submitEntry = async (e) => {
    e.preventDefault();
    const amt = Number(entryForm.amount) || 0;
    if (amt <= 0) { toast.error("Tutar pozitif olmalı"); return; }
    try {
      if (entryForm.type === "borc") {
        // Sales invoice = kesilen fatura → alacak
        await api.post("/sales-invoices", {
          invoice_no: entryForm.invoice_no || `SATIS-${Date.now()}`,
          company_id: selected.id, company_name: selected.name,
          date: entryForm.date, amount: amt,
          status: "issued", notes: entryForm.notes,
        });
        toast.success("Kesilen fatura eklendi (alacak arttı)");
      } else {
        await api.post("/incoming-payments/quick", {
          company_id: selected.id, company_name: selected.name,
          amount: amt, date: entryForm.date, notes: entryForm.notes || "Manuel tahsilat",
        });
        toast.success("Tahsilat eklendi");
      }
      setShowEntry(false);
      setEntryForm({ type: "borc", date: new Date().toISOString().slice(0,10), amount: "", notes: "", invoice_no: "" });
      load(); openStatement(selected);
    } catch (err) { toast.error(errMsg(err)); }
  };

  useEffect(() => {
    if (!mutCompany) return;
    (async () => {
      setMutLoading(true);
      try {
        const { data } = await api.get(`/companies/${mutCompany.id}/mutabakat-data?month=${mutMonth}`);
        setMutData(data);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setMutLoading(false); }
    })();
  }, [mutCompany, mutMonth]); // eslint-disable-line

  if (!summary) return <div className="p-8 text-sm text-neutral-500">Yükleniyor...</div>;

  const q = fold(search.trim());
  const filtered = summary.companies.filter(c => {
    if (onlyReceivable && c.balance <= 0) return false;
    if (!q) return true;
    return fold((c.name || "") + " " + (c.sector || "") + " " + (c.phone || "") + " " + (c.contact_person || "")).includes(q);
  });

  const openEditEvent = (evt) => {
    setEditingEvt(evt);
    setEditForm({
      date: evt.date || "",
      amount: String(evt.debit || evt.credit || evt.amount || 0),
      notes: evt.description || "",
      invoice_no: evt.reference || "",
    });
  };

  const submitEditEvent = async (e) => {
    e.preventDefault();
    const amt = Number(editForm.amount) || 0;
    if (amt <= 0) { toast.error("Tutar pozitif olmalı"); return; }
    try {
      if (editingEvt.type === "sales_invoice") {
        await api.put(`/sales-invoices/${editingEvt.id}`, {
          invoice_no: editForm.invoice_no || editingEvt.reference || "",
          company_id: selected.id, company_name: selected.name,
          date: editForm.date, amount: amt,
          status: editingEvt.status || "issued", notes: editForm.notes,
        });
      } else {
        await api.put(`/incoming-payments/${editingEvt.id}`, {
          company_id: selected.id, company_name: selected.name,
          invoice_no: editForm.invoice_no || "",
          date: editForm.date, amount: amt,
          status: "received", notes: editForm.notes,
        });
      }
      toast.success("Hareket güncellendi");
      setEditingEvt(null);
      load(); openStatement(selected);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteEvent = async (evt) => {
    const kind = evt.type === "sales_invoice" ? "kesilen faturayı" : "tahsilatı";
    if (!window.confirm(`Bu ${kind} silmek istediğinize emin misiniz?`)) return;
    try {
      const endpoint = evt.type === "sales_invoice" ? "/sales-invoices" : "/incoming-payments";
      await api.delete(`${endpoint}/${evt.id}`);
      toast.success("Silindi");
      load(); openStatement(selected);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openEditCompany = (c) => {
    setEditingCompany(c);
    setCompanyForm({
      name: c.name || "", sector: c.sector || "",
      contact_person: c.contact_person || "", phone: c.phone || "",
      email: c.email || "", tax_office: c.tax_office || "",
      tax_no: c.tax_no || "", payment_terms: c.payment_terms || "",
      notes: c.notes || "",
    });
  };
  const submitCompanyEdit = async (e) => {
    e.preventDefault();
    if (!companyForm.name.trim()) { toast.error("Firma adı zorunlu"); return; }
    try {
      await api.put(`/companies/${editingCompany.id}`, {
        ...companyForm,
        status: editingCompany.status || "active",
        address: editingCompany.address || "",
      });
      toast.success("Firma güncellendi");
      setEditingCompany(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const deleteCompany = async (c) => {
    if (!window.confirm(`"${c.name}" firmasını silmek istediğinize emin misiniz? Firma silinirse buna bağlı faturalar/tahsilatlar kalır ama listede firmasız görünür.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success("Firma silindi");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // ─── Mutabakat Mektubu ───
  const monthOptions = (() => {
    const arr = [];
    const d = new Date();
    for (let i = 0; i < 18; i++) {
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const names = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
      arr.push({ value: m, label: `${names[d.getMonth()]} ${d.getFullYear()}` });
      d.setMonth(d.getMonth() - 1);
    }
    return arr;
  })();

  const openMutabakat = async (c) => {
    setMutCompany(c);
    setMutMonth(new Date().toISOString().slice(0, 7));
    setMutData(null);
    setShowProfile(false);
    if (!profile) {
      try { const { data } = await api.get("/settings/company-profile"); setProfile(data); } catch { /* opsiyonel */ }
    }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await api.put("/settings/company-profile", profile);
      toast.success("Şirket bilgileri kaydedildi");
      setShowProfile(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setProfileSaving(false); }
  };

  const mutPdfUrl = () =>
    `${API}/companies/${mutCompany.id}/mutabakat-pdf?month=${mutMonth}&auth=${localStorage.getItem("token") || ""}`;


  return (
    <>
      <PageHeader
        title="Müşteri Cari Hesaplar"
        subtitle="Firmalara kesilen faturalar, gelen tahsilatlar ve alacak bakiyeleri. Kronolojik ekstre görünümü."
        testid="musteri-cari-header"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="btn-open-firma-match"
              onClick={openMatch}
              className="inline-flex items-center gap-2 bg-[#7C3AED] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#6D28D9]"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Cari ↔ Banka Karşılaştır
            </button>
            <button
              data-testid="btn-open-firma-recon"
              onClick={openRecon}
              className="inline-flex items-center gap-2 bg-[#0020C2] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#0018A0]"
            >
              <Landmark className="w-3.5 h-3.5" /> Banka'dan Firma Ödemesi İşle
            </button>
            <button
              data-testid="btn-purge-toplu-tahsilat"
              onClick={async () => {
                if (!window.confirm('Notunda "Toplu tahsilat" geçen TÜM gelen ödeme kayıtları silinecek ve ilgili faturalar tekrar "Bekleyen" olarak işaretlenecek. Bu işlem GERİ ALINAMAZ. Devam edilsin mi?')) return;
                if (!window.confirm("Son onay: Tüm 'Toplu tahsilat' kayıtlarını sil?")) return;
                try {
                  const { data } = await api.post("/admin/purge-toplu-tahsilat");
                  toast.success(`${data.deleted_payments} tahsilat silindi (₺${data.total_amount.toLocaleString("tr-TR")}) · ${data.reset_invoices} fatura sıfırlandı`);
                  load();
                } catch (e) { toast.error(errMsg(e)); }
              }}
              className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-3 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#B91C1C]"
            >
              <Eraser className="w-3.5 h-3.5" /> &quot;Toplu tahsilat&quot; Sil
            </button>
            <ExportButtons
            filename="musteri_cari"
            title="Müşteri Cari Bakiye"
            subtitle={`${summary.companies.length} müşteri · Alacak: ${formatTL(summary.grand.balance)} · Ödenmemiş: ${formatTL(summary.grand.outstanding)}`}
            sections={[
              { name: "Cari Bakiye", heading: "Müşteri Cari Bakiye",
                columns: [
                  { key: "name", label: "Müşteri" },
                  { key: "sector", label: "Sektör" },
                  { key: "sales_count", label: "Fat. Sayısı", format: "num" },
                  { key: "sales_total", label: "Kesilen Fatura", format: "money" },
                  { key: "incoming_total", label: "Tahsilat", format: "money" },
                  { key: "balance", label: "Alacak Bakiye", format: "money" },
                ],
                rows: summary.companies,
              },
            ]}
          />
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Müşteri</div>
          <div className="num font-bold text-xl mt-1">{formatNumber(summary.companies.length)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kesilen Fatura</div>
          <div className="num font-bold text-xl mt-1">{formatTL(summary.grand.sales_total)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Tahsilat</div>
          <div className="num font-bold text-xl mt-1 text-[#16A34A]">{formatTL(summary.grand.incoming_total)}</div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Alacak Bakiye</div>
          <div className={`num font-bold text-xl mt-1 ${summary.grand.balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(summary.grand.balance)}</div>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] p-3 rounded-md mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-neutral-400" />
          <input data-testid="musteri-search" placeholder="Müşteri, sektör, telefon ara..." value={search} onChange={e => setSearch(e.target.value)} className="flex-1 px-2 py-1.5 text-sm border-0 focus:outline-none" />
        </div>
        <button onClick={() => setOnlyReceivable(!onlyReceivable)} data-testid="filter-only-receivable" className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border ${onlyReceivable ? "bg-[#DC2626] text-white border-[#DC2626]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
          Sadece Alacaklılar ({summary.companies.filter(c => c.balance > 0).length})
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <tr>
                {["Müşteri","Sektör","Fatura","Tahsilat","Alacak Bakiye","Durum",""].map((h, i) => (
                  <th key={h} className={`px-3 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 2 && i <= 4 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-sm text-neutral-500">Müşteri bulunamadı</td></tr>
              ) : filtered.map(c => {
                const b = badge(c.balance);
                return (
                  <tr key={c.id} className="border-b border-[#F5F5F5]" data-testid={`musteri-row-${c.id}`}>
                    <td className="px-3 py-3">
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-neutral-500">{c.contact_person} {c.phone && `· ${c.phone}`}</div>
                    </td>
                    <td className="px-3 py-3 text-neutral-600">{c.sector || "-"}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="num font-semibold">{formatTL(c.sales_total)}</div>
                      <div className="text-[10px] text-neutral-500">{c.sales_count} adet</div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="num font-semibold text-[#16A34A]">{formatTL(c.incoming_total)}</div>
                      <div className="text-[10px] text-neutral-500">{c.incoming_count} tahsilat</div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className={`num font-bold text-base ${c.balance > 0 ? "text-[#DC2626]" : c.balance < 0 ? "text-[#F59E0B]" : "text-[#0A0A0A]"}`}>{formatTL(Math.abs(c.balance))}</div>
                      {c.balance > 0 && <div className="text-[10px] text-[#DC2626]">Tahsil Edilecek</div>}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold ${b.cls}`}>{b.label}</span>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <button data-testid={`musteri-open-${c.id}`} onClick={() => openStatement(c)} className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold hover:bg-[#0020C2]">
                          <FileText className="w-3 h-3" /> Ekstre
                        </button>
                        <button data-testid={`musteri-mutabakat-${c.id}`} onClick={() => openMutabakat(c)} className="p-1.5 border border-[#E5E5E5] hover:bg-[#16A34A] hover:text-white hover:border-[#16A34A] transition-colors" title="Mutabakat mektubu (PDF)">
                          <FileSignature className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`musteri-edit-${c.id}`} onClick={() => openEditCompany(c)} className="p-1.5 border border-[#E5E5E5] hover:bg-[#0020C2] hover:text-white hover:border-[#0020C2] transition-colors" title="Firma bilgilerini düzenle">
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button data-testid={`musteri-delete-${c.id}`} onClick={() => deleteCompany(c)} className="p-1.5 border border-[#E5E5E5] hover:bg-[#DC2626] hover:text-white hover:border-[#DC2626] transition-colors" title="Firmayı sil">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Statement drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end p-0" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-[95vw] xl:max-w-6xl shadow-lg overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center justify-between z-10 gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Müşteri Ekstre</div>
                <div className="font-heading font-bold text-base md:text-lg truncate">{selected.name}</div>
                {selected.contact_person && <div className="text-[10px] text-neutral-500 mt-0.5 truncate">{selected.contact_person} · {selected.phone}</div>}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {statement && (
                  <ExportButtons
                    filename={`ekstre_${selected.name.replace(/[^a-zA-Z0-9]/g, "_")}`}
                    title={`Cari Ekstre — ${selected.name}`}
                    subtitle={`Kesilen Fatura: ${formatTL(statement.summary.sales_total)} · Tahsilat: ${formatTL(statement.summary.incoming_total)} · Bakiye: ${formatTL(statement.summary.balance)}`}
                    sections={[
                      { name: "Ekstre", heading: "Cari Hareket Ekstresi",
                        columns: [
                          { key: "date", label: "Tarih" },
                          { key: "label", label: "Tip / Açıklama" },
                          { key: "reference", label: "Ref" },
                          { key: "debit", label: "Borç", format: "money" },
                          { key: "credit", label: "Alacak", format: "money" },
                          { key: "running_balance", label: "Bakiye", format: "money" },
                        ],
                        rows: statement.events,
                      },
                    ]}
                  />
                )}
                <button data-testid="musteri-entry-btn" onClick={() => setShowEntry(true)} title="Cari Hareket" className="flex items-center gap-1.5 bg-[#0020C2] text-white px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold hover:bg-[#0018A0]">
                  <Plus className="w-3 h-3" /><span className="hidden md:inline">Cari Hareket</span>
                </button>
                <button data-testid="musteri-coll-btn" onClick={() => setShowColl(true)} title="Hızlı Tahsilat" className="flex items-center gap-1.5 bg-[#16A34A] text-white px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold hover:bg-[#15803D]">
                  <HandCoins className="w-3 h-3" /><span className="hidden md:inline">Hızlı Tahsilat</span>
                </button>
                <button onClick={() => setSelected(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {statementLoading ? (
                <div className="text-center py-12 text-sm text-neutral-500">Ekstre yükleniyor...</div>
              ) : !statement ? null : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kesilen Fatura</div>
                      <div className="num font-bold text-lg text-[#DC2626] mt-1">{formatTL(statement.summary.sales_total)}</div>
                      <div className="text-[10px] text-neutral-500">{statement.summary.sales_count} fatura</div>
                    </div>
                    <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Tahsilat</div>
                      <div className="num font-bold text-lg text-[#16A34A] mt-1">{formatTL(statement.summary.incoming_total)}</div>
                      <div className="text-[10px] text-neutral-500">{statement.summary.incoming_count} tahsilat</div>
                    </div>
                    <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Alacak Bakiye</div>
                      <div className={`num font-bold text-lg mt-1 ${statement.summary.balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(statement.summary.balance)}</div>
                      <div className="text-[10px] text-neutral-500">{statement.summary.balance > 0 ? "Tahsil edilecek" : "Denk / Fazla"}</div>
                    </div>
                  </div>

                  {statement.events.length === 0 ? (
                    <div className="text-center py-10 text-sm text-neutral-500 border border-dashed border-[#E5E5E5]">
                      Bu müşteri için henüz fatura veya tahsilat kaydı yok.
                    </div>
                  ) : (
                    <div className="overflow-x-auto -mx-4 md:-mx-6">
                      <table className="w-full text-xs min-w-[720px]">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                          <tr>
                            <th className="px-2 py-2 text-center text-[10px] uppercase tracking-widest text-neutral-500 font-semibold w-[70px]">İşlem</th>
                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tarih</th>
                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Tip</th>
                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Açıklama</th>
                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Ref</th>
                            <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Borç</th>
                            <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Alacak</th>
                            <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Bakiye</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statement.events.map(e => (
                            <tr key={e.type + e.id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA] group">
                              <td className="px-2 py-2 text-center whitespace-nowrap bg-[#FAFAFA] group-hover:bg-[#F5F5F5]">
                                <div className="inline-flex gap-0.5">
                                  <button
                                    data-testid={`evt-edit-${e.type}-${e.id}`}
                                    onClick={() => openEditEvent(e)}
                                    className="p-1.5 bg-white hover:bg-[#0020C2] hover:text-white text-neutral-700 rounded border border-[#E5E5E5] shadow-sm"
                                    title="Düzenle"
                                  ><Pencil className="w-3 h-3" /></button>
                                  <button
                                    data-testid={`evt-delete-${e.type}-${e.id}`}
                                    onClick={() => deleteEvent(e)}
                                    className="p-1.5 bg-white hover:bg-[#DC2626] hover:text-white text-neutral-700 rounded border border-[#E5E5E5] shadow-sm"
                                    title="Sil"
                                  ><Trash2 className="w-3 h-3" /></button>
                                </div>
                              </td>
                              <td className="px-3 py-2 num whitespace-nowrap">{formatDate(e.date)}</td>
                              <td className="px-3 py-2">
                                {e.type === "sales_invoice" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#DC2626]"><ArrowUpRight className="w-3 h-3" /> {e.label}</span>}
                                {e.type === "incoming" && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#16A34A]"><ArrowDownLeft className="w-3 h-3" /> {e.label}</span>}
                                {e.status === "paid" && <span className="ml-1 px-1.5 py-0.5 bg-[#DCFCE7] text-[#16A34A] text-[9px] font-semibold uppercase">Tahsil</span>}
                              </td>
                              <td className="px-3 py-2 text-neutral-600 max-w-[220px] truncate" title={e.description}>{e.description || "-"}</td>
                              <td className="px-3 py-2 font-mono text-[10px] text-neutral-500">{e.reference || "-"}</td>
                              <td className="px-3 py-2 text-right num text-[#DC2626] font-semibold whitespace-nowrap">{e.debit > 0 ? formatTL(e.debit) : "-"}</td>
                              <td className="px-3 py-2 text-right num text-[#16A34A] font-semibold whitespace-nowrap">{e.credit > 0 ? formatTL(e.credit) : "-"}</td>
                              <td className={`px-3 py-2 text-right num font-bold whitespace-nowrap ${e.running_balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(e.running_balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-[#FAFAFA] border-t-2 border-[#E5E5E5]">
                          <tr>
                            <td className="bg-[#FAFAFA]"></td>
                            <td colSpan={4} className="px-3 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">TOPLAM</td>
                            <td className="px-3 py-2 text-right num text-[#DC2626] font-bold whitespace-nowrap">{formatTL(statement.summary.sales_total)}</td>
                            <td className="px-3 py-2 text-right num text-[#16A34A] font-bold whitespace-nowrap">{formatTL(statement.summary.incoming_total)}</td>
                            <td className={`px-3 py-2 text-right num font-bold whitespace-nowrap ${statement.summary.balance > 0 ? "text-[#DC2626]" : "text-[#16A34A]"}`}>{formatTL(statement.summary.balance)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hızlı Tahsilat */}
      {showColl && selected && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowColl(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Hızlı Tahsilat</div>
                <div className="font-heading font-bold text-lg">{selected.name}</div>
              </div>
              <button onClick={() => setShowColl(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitCollection} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                <input type="number" step="any" required value={collForm.amount} onChange={e => setCollForm({...collForm, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                <input type="date" required value={collForm.date} onChange={e => setCollForm({...collForm, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Fatura No (opsiyonel)</label>
                <input value={collForm.invoice_no} onChange={e => setCollForm({...collForm, invoice_no: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Notlar</label>
                <input value={collForm.notes} onChange={e => setCollForm({...collForm, notes: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowColl(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D]">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manuel cari hareket */}
      {showEntry && selected && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowEntry(false)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cari Hareket Ekle</div>
                <div className="font-heading font-bold text-lg">{selected.name}</div>
              </div>
              <button onClick={() => setShowEntry(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitEntry} className="p-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Hareket Tipi *</label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <button type="button" onClick={() => setEntryForm({...entryForm, type: "borc"})} className={`flex items-center justify-center gap-2 px-3 py-3 text-sm uppercase tracking-widest font-semibold border-2 transition-colors ${entryForm.type === "borc" ? "border-[#DC2626] bg-[#FEE2E2] text-[#DC2626]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
                    <ArrowUpRight className="w-4 h-4" /> Kesilen Fatura
                  </button>
                  <button type="button" onClick={() => setEntryForm({...entryForm, type: "alacak"})} className={`flex items-center justify-center gap-2 px-3 py-3 text-sm uppercase tracking-widest font-semibold border-2 transition-colors ${entryForm.type === "alacak" ? "border-[#16A34A] bg-[#DCFCE7] text-[#16A34A]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
                    <ArrowDownLeft className="w-4 h-4" /> Tahsilat
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                  <input type="number" step="any" required value={entryForm.amount} onChange={e => setEntryForm({...entryForm, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                  <input type="date" required value={entryForm.date} onChange={e => setEntryForm({...entryForm, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
              </div>
              {entryForm.type === "borc" && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Fatura No</label>
                  <input value={entryForm.invoice_no} onChange={e => setEntryForm({...entryForm, invoice_no: e.target.value})} placeholder="Otomatik" className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Notlar</label>
                <textarea rows={2} value={entryForm.notes} onChange={e => setEntryForm({...entryForm, notes: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setShowEntry(false)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button type="submit" className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold text-white ${entryForm.type === "borc" ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-[#16A34A] hover:bg-[#15803D]"}`}>
                  {entryForm.type === "borc" ? "Fatura Ekle" : "Tahsilat Ekle"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cari Hareket Düzenle Modal */}
      {editingEvt && selected && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" onClick={() => setEditingEvt(null)}>
          <div className="bg-white w-full max-w-md rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                  {editingEvt.type === "sales_invoice" ? "Kesilen Fatura Düzenle" : "Tahsilat Düzenle"}
                </div>
                <div className="font-heading font-bold text-lg">{selected.name}</div>
              </div>
              <button data-testid="edit-evt-close" onClick={() => setEditingEvt(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitEditEvent} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tutar (₺) *</label>
                  <input data-testid="edit-evt-amount" type="number" step="any" required value={editForm.amount} onChange={e => setEditForm({...editForm, amount: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Tarih *</label>
                  <input data-testid="edit-evt-date" type="date" required value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">
                  {editingEvt.type === "sales_invoice" ? "Fatura No" : "Banka / Ödeme Yolu"}
                </label>
                <input value={editForm.invoice_no} onChange={e => setEditForm({...editForm, invoice_no: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Not / Açıklama</label>
                <textarea rows={2} value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setEditingEvt(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="edit-evt-save" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Firma Düzenle Modal */}
      {editingCompany && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4" onClick={() => setEditingCompany(null)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Firma Düzenle</div>
                <div className="font-heading font-bold text-lg">{editingCompany.name}</div>
              </div>
              <button data-testid="edit-company-close" onClick={() => setEditingCompany(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitCompanyEdit} className="p-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Firma Adı *</label>
                  <input required value={companyForm.name} onChange={e => setCompanyForm({...companyForm, name: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" data-testid="company-form-name" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Sektör</label>
                  <input value={companyForm.sector} onChange={e => setCompanyForm({...companyForm, sector: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Yetkili Kişi</label>
                  <input value={companyForm.contact_person} onChange={e => setCompanyForm({...companyForm, contact_person: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Telefon</label>
                  <input value={companyForm.phone} onChange={e => setCompanyForm({...companyForm, phone: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Email</label>
                  <input value={companyForm.email} onChange={e => setCompanyForm({...companyForm, email: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vade (ör. 5 GÜN)</label>
                  <input value={companyForm.payment_terms} onChange={e => setCompanyForm({...companyForm, payment_terms: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vergi Dairesi</label>
                  <input value={companyForm.tax_office} onChange={e => setCompanyForm({...companyForm, tax_office: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Vergi No</label>
                  <input value={companyForm.tax_no} onChange={e => setCompanyForm({...companyForm, tax_no: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm num" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Notlar</label>
                <textarea rows={2} value={companyForm.notes} onChange={e => setCompanyForm({...companyForm, notes: e.target.value})} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E5E5]">
                <button type="button" onClick={() => setEditingCompany(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Vazgeç</button>
                <button data-testid="company-form-save" type="submit" className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2]">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Karşılaştırma Modal (matching status) */}
      {showMatch && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowMatch(false)}>
          <div className="bg-white w-full max-w-7xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Firma Cari ↔ Banka Karşılaştırma</div>
                <div className="font-heading font-bold text-lg">Tahsilat & Banka Hareketi Eşleşme Raporu</div>
              </div>
              <button onClick={() => setShowMatch(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-3 text-xs text-neutral-700 flex items-center gap-2 flex-wrap">
                <span>Firma tahsilatları (incoming_payments) ile gelen banka hareketlerini eşleştirir. Aynı gün ±X ve aynı tutar ±₺ ile.</span>
                <div className="ml-auto flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Yıl</label>
                  <select value={matchYear} onChange={e => setMatchYear(Number(e.target.value))} className="px-2 py-1 text-xs border border-[#E5E5E5]">
                    {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Gün Tol</label>
                  <input type="number" min="0" max="30" value={matchDayTol} onChange={e => setMatchDayTol(Number(e.target.value))} className="w-14 px-2 py-1 text-xs border border-[#E5E5E5] num" />
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Tutar Tol ₺</label>
                  <input type="number" min="0" step="0.1" value={matchAmtTol} onChange={e => setMatchAmtTol(Number(e.target.value))} className="w-16 px-2 py-1 text-xs border border-[#E5E5E5] num" />
                  <button data-testid="btn-match-scan" onClick={openMatch} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold bg-[#7C3AED] text-white hover:bg-[#6D28D9]">
                    <RefreshCw className="w-3 h-3 inline mr-1" /> Tara
                  </button>
                </div>
              </div>

              {matchLoading && <div className="p-12 text-center"><RefreshCw className="w-6 h-6 mx-auto animate-spin text-[#7C3AED] mb-2" /><div className="text-xs text-neutral-500">Karşılaştırılıyor...</div></div>}

              {matchData && !matchLoading && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cari Tahsilat</div>
                      <div className="num font-bold text-base">{matchData.counts.total_payments}</div>
                      <div className="text-[10px] num text-neutral-500">{formatTL(matchData.totals.payments_total)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Gelen Banka Hrk</div>
                      <div className="num font-bold text-base">{matchData.counts.total_bank_incoming}</div>
                      <div className="text-[10px] num text-neutral-500">{formatTL(matchData.totals.bank_total)}</div>
                    </div>
                    <div className="border border-[#16A34A] bg-[#DCFCE7] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#16A34A] font-semibold">Eşleşen ✓</div>
                      <div className="num font-bold text-base text-[#16A34A]">{matchData.counts.matched}</div>
                      <div className="text-[10px] num text-[#16A34A]">{formatTL(matchData.totals.matched_total)}</div>
                    </div>
                    <div className="border border-[#F59E0B] bg-[#FEF3C7] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#B45309] font-semibold">Cari Var, Banka Yok ⚠️</div>
                      <div className="num font-bold text-base text-[#B45309]">{matchData.counts.cari_only}</div>
                      <div className="text-[10px] num text-[#B45309]">{formatTL(matchData.totals.cari_only_total)}</div>
                    </div>
                    <div className="border border-[#0020C2] bg-[#DBEAFE] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-[#0020C2] font-semibold">Banka Var, Cari Yok</div>
                      <div className="num font-bold text-base text-[#0020C2]">{matchData.counts.bank_only}</div>
                      <div className="text-[10px] num text-[#0020C2]">{formatTL(matchData.totals.bank_only_total)}</div>
                    </div>
                  </div>

                  {/* Sub-tabs */}
                  <div className="flex items-center gap-1 border-b border-[#E5E5E5]">
                    {[
                      { key: "cari_only", label: `⚠️ Cari Var, Banka Yok (${matchData.counts.cari_only})`, color: "text-[#B45309]" },
                      { key: "bank_only", label: `🔵 Banka Var, Cari Yok (${matchData.counts.bank_only})`, color: "text-[#0020C2]" },
                      { key: "matched", label: `✓ Eşleşen (${matchData.counts.matched})`, color: "text-[#16A34A]" },
                    ].map(t => (
                      <button key={t.key} onClick={() => setMatchTab(t.key)}
                        className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold border-b-2 -mb-px transition-colors ${
                          matchTab === t.key ? `border-[#0A0A0A] ${t.color}` : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"
                        }`}>
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {/* Table */}
                  <div className="border border-[#E5E5E5] rounded overflow-hidden">
                    <div className="max-h-[420px] overflow-y-auto">
                      {matchTab === "cari_only" && (
                        <table className="w-full text-xs">
                          <thead className="bg-[#FEF3C7] border-b border-[#E5E5E5] sticky top-0">
                            <tr>{["Tarih","Firma","Yöntem","Tutar (₺)","Not"].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-[#B45309] font-semibold">{h}</th>)}</tr>
                          </thead>
                          <tbody>
                            {matchData.cari_only.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-neutral-500">Yok — tüm tahsilatların banka karşılığı var 🎉</td></tr>}
                            {matchData.cari_only.map(p => (
                              <tr key={p.payment_id} className="border-t border-[#F5F5F5] hover:bg-[#FEF3C7]">
                                <td className="px-3 py-1.5 num whitespace-nowrap">{p.date}</td>
                                <td className="px-3 py-1.5 font-semibold">{p.company_name || "?"}</td>
                                <td className="px-3 py-1.5 text-neutral-600">{p.payment_method || "-"}</td>
                                <td className="px-3 py-1.5 num text-right text-[#B45309] font-bold">{formatTL(p.amount)}</td>
                                <td className="px-3 py-1.5 text-neutral-500 text-[11px] max-w-[300px] truncate">{p.note || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {matchTab === "bank_only" && (
                        <table className="w-full text-xs">
                          <thead className="bg-[#DBEAFE] border-b border-[#E5E5E5] sticky top-0">
                            <tr>{["Tarih","Açıklama","Karşı Taraf","Banka","Kategori","Tutar (₺)"].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-[#0020C2] font-semibold">{h}</th>)}</tr>
                          </thead>
                          <tbody>
                            {matchData.bank_only.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-neutral-500">Yok — tüm gelen banka hareketleri bir tahsilata karşılık geliyor 🎉</td></tr>}
                            {matchData.bank_only.map(b => (
                              <tr key={b.bank_tx_id} className="border-t border-[#F5F5F5] hover:bg-[#DBEAFE]">
                                <td className="px-3 py-1.5 num whitespace-nowrap">{b.date}</td>
                                <td className="px-3 py-1.5 max-w-[260px] truncate" title={b.description}>{b.description || "-"}</td>
                                <td className="px-3 py-1.5 text-neutral-600 max-w-[160px] truncate">{b.counterparty || "-"}</td>
                                <td className="px-3 py-1.5 text-neutral-500 text-[10px]">{b.bank_account || "-"}</td>
                                <td className="px-3 py-1.5 text-neutral-500 text-[10px]">{b.category || "(kategorisiz)"}</td>
                                <td className="px-3 py-1.5 num text-right text-[#0020C2] font-bold">{formatTL(b.amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {matchTab === "matched" && (
                        <table className="w-full text-xs">
                          <thead className="bg-[#DCFCE7] border-b border-[#E5E5E5] sticky top-0">
                            <tr>{["Trh (Cari)","Firma","Cari Tutar","→","Trh (Banka)","Banka Açıklama"].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-[#16A34A] font-semibold">{h}</th>)}</tr>
                          </thead>
                          <tbody>
                            {matchData.matched.map(p => (
                              <tr key={p.payment_id} className="border-t border-[#F5F5F5] hover:bg-[#DCFCE7]">
                                <td className="px-3 py-1.5 num whitespace-nowrap">{p.date}</td>
                                <td className="px-3 py-1.5 font-semibold">{p.company_name || "?"}</td>
                                <td className="px-3 py-1.5 num text-right text-[#16A34A] font-bold">{formatTL(p.amount)}</td>
                                <td className="px-3 py-1.5 text-[#16A34A]">✓</td>
                                <td className="px-3 py-1.5 num text-neutral-500">{p.matched_bank_date}</td>
                                <td className="px-3 py-1.5 text-neutral-600 max-w-[280px] truncate" title={p.matched_bank_desc}>{p.matched_bank_desc || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bank → Firma Recon Modal */}
      {showRecon && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRecon(false)}>
          <div className="bg-white w-full max-w-6xl rounded-md shadow-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Otomatik Eşleştirme</div>
                <div className="font-heading font-bold text-lg">Banka Hareketlerinden Firma Ödemesi İşle</div>
              </div>
              <button onClick={() => setShowRecon(false)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-[#F5F5F5] border border-[#E5E5E5] p-3 text-xs text-neutral-700 flex items-center gap-3 flex-wrap">
                <span>Gelen banka hareketlerini firma cariyle eşleştirir. Açıklamada firma adı geçen veya "fatura/hakediş/tahsilat/havale" gibi kelimelerin olduğu hareketler gösterilir.</span>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Yıl</span>
                  <select value={reconYear} onChange={e => setReconYear(Number(e.target.value))} className="px-2 py-1 text-xs border border-[#E5E5E5]">
                    {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <button data-testid="btn-scan-firma-recon" onClick={openRecon} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0018A0]">
                    <RefreshCw className="w-3 h-3 inline mr-1" /> Tara
                  </button>
                </div>
              </div>

              {reconLoading && (
                <div className="p-12 text-center"><RefreshCw className="w-6 h-6 mx-auto animate-spin text-[#0020C2] mb-2" /><div className="text-xs text-neutral-500">Banka hareketleri taranıyor...</div></div>
              )}

              {reconData && !reconLoading && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Bulunan</div>
                      <div className="num font-bold text-lg">{reconData.matched_count}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Tutar</div>
                      <div className="num font-bold text-lg text-[#16A34A]">{formatTL(reconData.matched_amount)}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Toplam Gelen Banka</div>
                      <div className="num font-bold text-lg">{reconData.total_incoming_bank_txs}</div>
                    </div>
                    <div className="border border-[#E5E5E5] p-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Firma Sayısı</div>
                      <div className="num font-bold text-lg">{reconData.companies_count}</div>
                    </div>
                  </div>

                  <div className="border border-[#E5E5E5] rounded overflow-hidden">
                    <div className="px-4 py-2.5 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">Önizleme · Seçili: {Object.values(reconSelected).filter(Boolean).length} / {reconData.matches.length}</span>
                      <button data-testid="btn-apply-firma-recon" onClick={applyRecon} disabled={Object.values(reconSelected).filter(Boolean).length === 0}
                        className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#16A34A] text-white hover:bg-[#15803D] disabled:opacity-40 flex items-center gap-2">
                        <Link2 className="w-3.5 h-3.5" /> Seçilileri Uygula
                      </button>
                    </div>
                    <div className="max-h-[500px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-white border-b border-[#F5F5F5] sticky top-0">
                          <tr>
                            {["Tarih","Açıklama","Karşı Taraf","Tutar (₺)","Puan","İşaretler","Firma Seç"].map(h => (
                              <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reconData.matches.length === 0 && (
                            <tr><td colSpan={7} className="text-center py-8 text-neutral-500">Eşleşme bulunamadı.</td></tr>
                          )}
                          {reconData.matches.map(m => (
                            <tr key={m.bank_tx_id} className={`border-t border-[#F5F5F5] hover:bg-[#FAFAFA] ${reconSelected[m.bank_tx_id] ? "bg-[#DCFCE7]" : ""}`}>
                              <td className="px-3 py-1.5 num whitespace-nowrap">{m.bank_date}</td>
                              <td className="px-3 py-1.5 max-w-[260px] truncate" title={m.bank_desc}>{m.bank_desc}</td>
                              <td className="px-3 py-1.5 text-neutral-600">{m.bank_counterparty || "-"}</td>
                              <td className="px-3 py-1.5 num text-right text-[#16A34A] font-semibold">{formatTL(m.bank_amount)}</td>
                              <td className="px-3 py-1.5"><span className={`text-[10px] font-bold ${m.confidence >= 60 ? "text-[#16A34A]" : "text-[#F59E0B]"}`}>{m.confidence}</span></td>
                              <td className="px-3 py-1.5">
                                {m.detected_company_name && <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 bg-[#DBEAFE] text-[#0020C2] mr-1">İSİM</span>}
                                {m.has_invoice_keyword && <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 bg-[#FEF3C7] text-[#B45309]">FATURA KW</span>}
                              </td>
                              <td className="px-3 py-1.5">
                                <select value={reconSelected[m.bank_tx_id] || ""} onChange={e => setReconSelected({ ...reconSelected, [m.bank_tx_id]: e.target.value })}
                                  className="px-2 py-1 text-xs border border-[#E5E5E5] bg-white max-w-[240px]">
                                  <option value="">— Seçim yok (atla) —</option>
                                  {(summary.companies || []).map(c => (
                                    <option key={c.id} value={c.id}>{c.name}{m.detected_company_id === c.id ? " ✓" : ""}</option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mutabakat Mektubu Modal */}
      {mutCompany && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setMutCompany(null)}>
          <div className="bg-white w-full max-w-xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mutabakat-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Cari Hesap Mutabakat Mektubu</div>
                <div className="font-heading font-bold text-lg">{mutCompany.name}</div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="mutabakat-profile-btn" onClick={() => setShowProfile(!showProfile)} className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border transition-colors ${showProfile ? "bg-[#0020C2] text-white border-[#0020C2]" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"}`}>
                  <Settings2 className="w-3.5 h-3.5" /> Şirket Bilgileri
                </button>
                <button onClick={() => setMutCompany(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {showProfile && profile && (
                <div className="border border-[#0020C2]/30 bg-[#F5F8FF] p-4 space-y-3">
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-[#0020C2]">KESKİNTUR Bilgileri (PDF alt bilgisinde kullanılır)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      ["name", "Şirket Ünvanı", "md:col-span-2"],
                      ["address", "Adres", "md:col-span-2"],
                      ["phone", "Telefon", ""],
                      ["fax", "Fax", ""],
                      ["email", "E-posta", ""],
                      ["tax_office", "Vergi Dairesi", ""],
                      ["tax_no", "Vergi No", ""],
                      ["trade_registry", "Ticaret Sicil No", ""],
                    ].map(([k, l, span]) => (
                      <div key={k} className={span}>
                        <label className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold">{l}</label>
                        <input data-testid={`profile-${k}`} value={profile[k] || ""} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} className="mt-1 w-full px-2.5 py-1.5 bg-white border border-[#E5E5E5] text-sm" />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <button data-testid="profile-save-btn" disabled={profileSaving} onClick={saveProfile} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50 transition-colors">
                      {profileSaving ? "Kaydediliyor..." : "Kaydet"}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Mutabakat Dönemi (Ay Sonu İtibarıyla)</label>
                <select data-testid="mutabakat-month" value={mutMonth} onChange={(e) => setMutMonth(e.target.value)} className="mt-1.5 w-full px-3 py-2 bg-white border border-[#E5E5E5] text-sm">
                  {monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>

              {mutLoading || !mutData ? (
                <div className="p-6 text-center text-sm text-neutral-500 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Bakiye hesaplanıyor...</div>
              ) : (
                <div className="border border-[#E5E5E5]">
                  <div className="grid grid-cols-3 divide-x divide-[#F5F5F5]">
                    <div className="p-3.5">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kesilen Fatura</div>
                      <div className="num font-bold text-sm mt-1">{formatTL(mutData.sales_total)}</div>
                    </div>
                    <div className="p-3.5">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Gelen Tahsilat</div>
                      <div className="num font-bold text-sm mt-1 text-[#16A34A]">{formatTL(mutData.payments_total)}</div>
                    </div>
                    <div className="p-3.5 bg-[#FAFAFA]">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{formatDate(mutData.month_end)} Bakiye</div>
                      <div className={`num font-bold text-sm mt-1 ${mutData.direction === "borc" ? "text-[#DC2626]" : "text-[#F59E0B]"}`} data-testid="mutabakat-balance">
                        {formatTL(Math.abs(mutData.balance))} <span className="text-[9px] uppercase tracking-widest">{mutData.direction === "borc" ? "BORÇ" : "ALACAK"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="px-3.5 py-2.5 border-t border-[#F5F5F5] text-[11px] text-neutral-500">
                    Mektupta: "Cari hesabınız {formatDate(mutData.month_end)} tarihi itibari ile {formatTL(Math.abs(mutData.balance)).replace("₺", "")} TL {mutData.direction === "borc" ? "BORÇ" : "ALACAK"} bakiyesi vermektedir..."
                  </div>
                </div>
              )}

              {profile && !profile.address && !profile.phone && (
                <div className="text-[11px] text-[#F59E0B] bg-[#FFFBEB] border border-[#FDE68A] p-2.5">
                  İpucu: "Şirket Bilgileri" bölümünden adres/telefon girerseniz PDF'in alt bilgisinde görünür.
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setMutCompany(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">Kapat</button>
                <a
                  data-testid="mutabakat-pdf-btn"
                  href={mutData ? mutPdfUrl() : undefined}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#16A34A] transition-colors ${!mutData ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Download className="w-3.5 h-3.5" /> PDF İndir
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
